import { DateTime } from 'luxon';
import PropTypes from 'prop-types';
import React, { useRef, useState } from 'react';
import DatePicker from 'react-datepicker';
import { FormattedMessage, useIntl } from 'react-intl';
import { Button, ButtonGroup } from 'reactstrap';

/**
 * DatePicker component for the trade list table
 * @param {object} root0
 * @param {DateTime} root0.date - date selected (to view the trades)
 * @param {Function} root0.dateUpdateFunc - to update the user date selection
 * @param {boolean} root0.isModalOpen - indicate whether the loading modal is open or not
 * @returns {React.ReactComponentElement} - TradeListDatePicker component
 */
function TradeListDatePicker({
  date, dateUpdateFunc, isModalOpen = false,
}) {
  const intl = useIntl();
  const [selectedDate, setSelectedDate] = useState(date);
  const [appliedDate, setAppliedDate] = useState(date);
  const datePickerRef = useRef(null);

  // Reset the in-calendar draft selection whenever the applied date prop changes
  // externally (e.g. after an error is dismissed and the previous successful date
  // is restored). Adjusting state during render is the "You Might Not Need an
  // Effect" replacement for syncing local state to a prop.
  // Use millisecond comparison instead of reference equality to avoid triggering
  // a state update (and a subsequent re-render) when the parent passes a new
  // DateTime instance that represents the same point in time.
  if (!appliedDate || date.toMillis() !== appliedDate.toMillis()) {
    setAppliedDate(date);
    setSelectedDate(date);
  }

  const closeCalendar = () => {
    datePickerRef.current?.setOpen(false);
  };

  const dateButtonLabel = (d) => (d.setLocale(intl.locale).toFormat('d MMM yyyy'));

  const applyDate = () => {
    dateUpdateFunc(selectedDate);
  };

  const resetDraft = () => {
    setSelectedDate(date);
  };

  const updateDate = (d) => {
    const newDate = d && DateTime.fromJSDate(d);
    if (newDate && date && newDate.toSeconds() === date.toSeconds()) {
      return;
    }
    setSelectedDate(newDate);
  };

  const renderDatePicker = () => (
    <DatePicker
      locale={intl.locale}
      ref={datePickerRef}
      value={dateButtonLabel(date)}
      selected={selectedDate ? selectedDate.toJSDate() : null}
      onChange={updateDate}
      onCalendarClose={resetDraft}
      customInput={<DateButton isModalOpen={isModalOpen} />}
      shouldCloseOnSelect={false}
      popperPlacement="bottom-end"
    >
      <div
        style={{
          clear: 'both',
          textAlign: 'right',
          borderTop: '1px solid #ccc',
          padding: '1em',
        }}
      >
        <button className="btn btn-primary" type="button" onClick={() => { applyDate(); closeCalendar(); }}>
          <FormattedMessage
            id="trade.trade_list.trade_list_controls.date_picker.apply_button.label"
            defaultMessage="Apply"
          />
        </button>
      </div>
    </DatePicker>
  );

  return (
    <ButtonGroup className="me-2 mb-2 trades-history-date-picker">
      {renderDatePicker()}
    </ButtonGroup>
  );
}

TradeListDatePicker.propTypes = {
  date: PropTypes.instanceOf(DateTime).isRequired,
  dateUpdateFunc: PropTypes.func.isRequired,
  isModalOpen: PropTypes.bool,
};
export default TradeListDatePicker;

// Ref: <https://github.com/Hacker0x01/react-datepicker/issues/862>
//
// eslint-disable-next-line react/prefer-stateless-function
class DateButton extends React.Component {
  render() {
    const {
      onClick,
      disabled,
      value,
      isModalOpen,
    } = this.props;

    return (
      <Button size="sm" className="btn btn-darken" onClick={() => (onClick())} disabled={disabled || isModalOpen}>
        {value}
      </Button>
    );
  }
}

DateButton.propTypes = {
  disabled: PropTypes.bool,
  isModalOpen: PropTypes.bool,
  onClick: PropTypes.func,
  // Always supplied by react-datepicker when it clones `customInput`.
  value: PropTypes.string.isRequired,
};
DateButton.defaultProps = {
  disabled: false,
  isModalOpen: false,
  onClick: null,
};

export { DateButton };
